import { useState, useRef, useEffect } from 'react';
import type { Panel as PanelType } from '@/types/dashboard';
import { t } from '@/i18n';
import { WidgetRenderer } from './WidgetRenderer';

interface PanelProps {
  panel: PanelType;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onDuplicate?: (id: string) => void;
  isEditable?: boolean;
  bordered?: boolean;
}

export function Panel({ panel, bordered = true, onEdit, onDelete, onDuplicate, isEditable = false }: PanelProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const isLoading = panel._isLoading === true;
  const isLoaded = panel._dataLoaded === true;
  const hasError = !isLoading && panel._loadError != null;
  const showActions = isEditable && onEdit !== undefined;

  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const renderContent = () => {
    const inner = (() => {
      if (isLoading) {
        return (
          <div className="d-flex align-items-center justify-content-center">
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t('dashboard.loading')}</span>
              </div>
            </div>
          </div>
        );
      }

      if (hasError) {
        return (
          <div className="d-flex align-items-center justify-content-center">
            <div className="text-center text-danger">
              <i className="las la-exclamation-triangle fa-2x mb-2"></i>
              <p className="mb-0">{panel._loadError}</p>
            </div>
          </div>
        );
      }

      if (isLoaded) {
        return (
          <div data-testid="widget-content" style={{height: '100%'}}>
            <WidgetRenderer panel={panel} />
          </div>
        );
      }

      return (
        <div className="d-flex align-items-center justify-content-center">
          <div className="text-center text-muted">
            <i className="las la-layer-group fa-2x mb-2"></i>
            <p className="mb-0 small">{t('dashboard.noData')}</p>
          </div>
        </div>
      );
    })();

    if (bordered) {
      return <div className="card-body">{inner}</div>;
    }
    return inner;
  };

  return (
    <div className="position-relative h-100">
      {showActions && (
        <div className="panel-actions" ref={menuRef}>
          <button
            type="button"
            className="btn btn-sm panel-actions-btn border-0"
            data-testid="panel-actions-btn"
            onClick={() => setMenuOpen((prev) => !prev)}
          >
            <i className="las la-cog fs-5"></i>
          </button>
          {menuOpen && (
            <div className="dropdown-menu show panel-actions-menu">
              <button
                type="button"
                className="dropdown-item"
                data-testid="panel-edit-btn"
                onClick={() => {
                  onEdit(panel.id);
                  setMenuOpen(false);
                }}
              >
                <i className="las la-pen me-2"></i>{t('dashboard.edit')}
              </button>
              <button
                type="button"
                className="dropdown-item"
                onClick={() => {
                  onDuplicate?.(panel.id);
                  setMenuOpen(false);
                }}
              >
                <i className="las la-copy me-2"></i>{t('dashboard.createCopy')}
              </button>
              <hr className="dropdown-divider" />
              <button
                type="button"
                className="dropdown-item text-danger"
                data-testid="panel-delete-btn"
                onClick={() => {
                  onDelete?.(panel.id);
                  setMenuOpen(false);
                }}
              >
                <i className="las la-trash me-2"></i>{t('dashboard.delete')}
              </button>
            </div>
          )}
        </div>
      )}

      {bordered ? (
        <div className="card h-100 d-flex flex-column">
          <div className="card-header d-flex justify-content-between" style={{ display: panel.title ? undefined : 'none' }}>
            <div className="header-title">
              {panel.title && <h6 className="card-title mb-0">{panel.title}</h6>}
            </div>
          </div>
          {renderContent()}
        </div>
      ) : (
        renderContent()
      )}
    </div>
  );
}

export default Panel;
